import {
  DataSchemaType,
  Dataset,
  DatasetDataType,
  DatasetSchemaLabel
} from "@/types/dataset";
import { SelectedFeature } from "@/types/map";
import { formatNumber } from "@/utils/number";
import { tryParseJsonArray } from "@/utils/string";

import {
  CATCHMENT_ID_ATTRIBUTE,
  GRID_ID_ATTRIBUTE,
  POLYGON_ID_ATTRIBUTE
} from "../analysisSummary/filterByGrid";
import { TabItem } from "../tabSelect";
import { FeaturePhotoItem, FeaturePhotoValueType } from "./types";

export type FeatureLinkItem = {
  url: string;
  label?: string;
};

export type DisplayAttribute = {
  key: string;
  value:
    | string
    | number
    | boolean
    | (string | number | boolean)[]
    | null
    | undefined;
  dataType?: DataSchemaType | DatasetSchemaLabel;
  originalIndex: number;
  photos?: FeaturePhotoItem[];
};

export type GroupedAttributes = {
  primary: DisplayAttribute[];
  secondary: DisplayAttribute[];
};

export const findItemById = (
  obj: {
    [id: string]: Dataset;
  },
  id: string | undefined
): Dataset | null => {
  const KEY = ["-point", "-polygon"];

  if (!id || !obj) return null;

  // Remove any value in KEY from the end of id
  let baseId = id;
  for (const suffix of KEY) {
    if (baseId.endsWith(suffix)) {
      baseId = baseId.slice(0, -suffix.length);
      break;
    }
  }

  return obj[baseId] || null;
};

export const getAttributePriority = (
  dataType?: DataSchemaType | DatasetSchemaLabel
) => {
  if (dataType === DataSchemaType.PHOTOS) return 0;
  if (dataType === DataSchemaType.LINKS) return 1;
  return 2;
};

export const getPhotoTypePriority = (type: FeaturePhotoValueType): number => {
  switch (type) {
    case FeaturePhotoValueType.ARRAY_OBJECT:
      return 0;
    case FeaturePhotoValueType.ARRAY_VALUE:
      return 1;
    case FeaturePhotoValueType.SINGLE:
    default:
      return 2;
  }
};

/**
 * Determines if an attribute should be displayed in the primary section
 */
const isPrimaryAttribute = (
  dataType?: DataSchemaType | DatasetSchemaLabel
): boolean => {
  return (
    dataType === DataSchemaType.PHOTOS ||
    dataType === DataSchemaType.ADDRESS ||
    dataType === DataSchemaType.LINKS
  );
};

/**
 * Groups display attributes into primary and secondary sections
 */
export const groupAttributesByPriority = (
  displayAttributes: DisplayAttribute[],
  adminAreaLabelKeys: Set<string>
): GroupedAttributes => {
  return displayAttributes.reduce<GroupedAttributes>(
    (groups, attribute) => {
      const { dataType, key } = attribute;

      if (isPrimaryAttribute(dataType)) {
        groups.primary.push(attribute);
      } else if (!adminAreaLabelKeys.has(key)) {
        groups.secondary.push(attribute);
      }

      return groups;
    },
    { primary: [], secondary: [] }
  );
};

/**
 * Determines the display order priority for primary attributes
 */
export const getPrimaryAttributeOrder = (
  attribute: DisplayAttribute
): number => {
  const { dataType, key } = attribute;

  if (dataType === DataSchemaType.PHOTOS) return 0;
  if (dataType === DataSchemaType.ADDRESS) return 1;
  if (key === "adm_area") return 2;
  if (dataType === DataSchemaType.LINKS) return 3;
  return 4;
};

/**
 * Sorts primary attributes by type order and original position
 */
export const sortPrimaryAttributes = (
  attributes: DisplayAttribute[]
): DisplayAttribute[] => {
  const sorted = [...attributes];
  sorted.sort((a, b) => {
    const typeOrderDiff =
      getPrimaryAttributeOrder(a) - getPrimaryAttributeOrder(b);
    if (typeOrderDiff !== 0) return typeOrderDiff;
    return a.originalIndex - b.originalIndex;
  });
  return sorted;
};

/**
 * Builds a virtual admin area attribute from individual admin area keys
 */
export const buildAdminAreaAttribute = (
  adminAreaKeys: string[],
  attributeEntriesByNormalizedKey: Map<
    string,
    { key: string; value: unknown; index: number }
  >,
  normalizeKey: (key: string) => string,
  resultTransform: Record<string, unknown>
): DisplayAttribute | null => {
  let adminAreaReferenceIndex = Number.MAX_SAFE_INTEGER;

  const adminAreaValues = adminAreaKeys
    .map((rawKey) => {
      const entry = attributeEntriesByNormalizedKey.get(normalizeKey(rawKey));
      if (!entry) return null;

      const { key: originalKey, value, index } = entry;

      const transformedValue = Object.prototype.hasOwnProperty.call(
        resultTransform,
        originalKey
      )
        ? resultTransform[originalKey]
        : value;

      if (transformedValue === null || transformedValue === undefined) {
        return null;
      }

      if (index < adminAreaReferenceIndex) {
        adminAreaReferenceIndex = index;
      }

      return transformedValue;
    })
    .filter((value): value is string => typeof value === "string");

  if (adminAreaValues.length === 0) {
    return null;
  }

  return {
    key: "adm_area",
    value: adminAreaValues,
    dataType: undefined,
    originalIndex: adminAreaReferenceIndex
  };
};

export const isArrayOfObjectsString = (val: unknown): val is string => {
  const parsed = tryParseJsonArray(val);
  return (
    parsed !== null &&
    parsed.length > 0 &&
    typeof parsed[0] === "object" &&
    parsed[0] !== null
  );
};

export const isArrayOfPrimitivesString = (val: unknown): val is string => {
  const parsed = tryParseJsonArray(val);
  return parsed !== null && parsed.length > 0 && typeof parsed[0] !== "object";
};

export const getDisplayValue = (
  val: unknown,
  dataType?: DataSchemaType | DatasetSchemaLabel
) => {
  if (Array.isArray(val)) return val.join("\n");

  const isNumeric =
    dataType === DataSchemaType.FLOAT || dataType === DataSchemaType.INT;
  const numericValue = Number(val);
  if (isNumeric && val !== null && val !== "" && !Number.isNaN(numericValue)) {
    const formatType =
      dataType === DataSchemaType.FLOAT ? "decimal" : "integer";
    return String(formatNumber(numericValue, formatType));
  }
  const jsonArray = tryParseJsonArray(val);
  if (jsonArray !== null) {
    if (isArrayOfObjectsString(val)) return "";
    return jsonArray.join("\n");
  }
  return String(val);
};

type FilterState = {
  query: string[] | null;
  query_type: DatasetDataType[] | null;
  query_column: string[] | null;
};

export const GEOM_ID_ATTRIBUTES = [
  GRID_ID_ATTRIBUTE,
  CATCHMENT_ID_ATTRIBUTE,
  POLYGON_ID_ATTRIBUTE
] as const;

export type SelectedIdType = "grid_id" | "catchment_id" | "polygon_id";

export type SelectedId = { type: SelectedIdType; id: string } | null;

export const GEOM_ID_ATTRIBUTE_TO_TYPE: Record<
  GeomIdAttribute,
  SelectedIdType
> = {
  [GRID_ID_ATTRIBUTE]: "grid_id",
  [CATCHMENT_ID_ATTRIBUTE]: "catchment_id",
  [POLYGON_ID_ATTRIBUTE]: "polygon_id"
};

export type GeomIdAttribute = (typeof GEOM_ID_ATTRIBUTES)[number];

export const findAttributeValue = (
  properties: Record<string, unknown>,
  attributeName: string
): string | undefined => {
  const key = Object.keys(properties).find(
    (k) => k.toLowerCase() === attributeName.toLowerCase()
  );

  const value = key ? properties[key] : undefined;
  return typeof value === "string" ? value : undefined;
};

export const getGeomIdOfSelectedFeature = (
  selectedFeature: SelectedFeature | null
): SelectedId => {
  if (!selectedFeature?.properties) return null;

  const props = selectedFeature.properties as Record<string, unknown>;

  for (const attribute of GEOM_ID_ATTRIBUTES) {
    const value = findAttributeValue(props, attribute);

    if (value) {
      return {
        type: GEOM_ID_ATTRIBUTE_TO_TYPE[attribute],
        id: value
      };
    }
  }

  return null;
};

const PREFIX_SUFFIX_REGEX =
  /^(?:(count|sum|avg|average|min|max|total)\s+of\s+)?([^\s]+(?:\s+[^\s]+)*)(?:\s+(proportion|percentage|percent|ratio|share))?$/i;

export const normalizeQueryColumnToDatasetName = (value: string): string => {
  if (!value) return "";
  let result = value;
  const match = result.match(PREFIX_SUFFIX_REGEX);
  if (match?.[2]) {
    result = match[2];
  }

  return result.trim();
};

const toComparableText = (value: string) =>
  value.replaceAll("_", " ").toLowerCase().trim();

export const getTabIdFromFilterState = (
  filterState: FilterState,
  tabs: TabItem[]
): string | null => {
  if (!filterState.query_column?.length) return null;

  const queryColumn = filterState.query_column[0];

  const normalizedQuery = normalizeQueryColumnToDatasetName(queryColumn);

  const matchedTab = tabs.find((tab) => {
    const comparableTab = toComparableText(tab.label);
    const comparableQuery = toComparableText(normalizedQuery);

    return (
      comparableQuery.includes(comparableTab) ||
      comparableTab.includes(comparableQuery)
    );
  });

  return matchedTab?.value ?? null;
};
